import { useCallback, useState } from 'react';

/*
    🔥 ESTADO DEL TABLERO

    columns: [{ id, label, tone, limit }]
    cards:   [{ id, columnId, title, … , order }]

    ⚠️ EL ORDEN ES UN NÚMERO, NO LA POSICIÓN EN EL ARRAY

    Si el orden fuera el índice, mover una tarjeta obligaría a
    reescribir el campo de TODAS las de la columna y mandar veinte
    updates al backend. Con un número, mover entre dos tarjetas es el
    promedio de sus órdenes: un solo registro cambia.

    ⚠️ EL MOVIMIENTO ES OPTIMISTA Y REVERSIBLE

    La tarjeta se mueve en pantalla al instante y después se avisa al
    backend. Si onMove falla, se vuelve al estado anterior.
*/

const STEP = 1000;

export const onNextOrder = (cards) => (
    cards.length ? Math.max(...cards.map((c) => c.order ?? 0)) + STEP : STEP
);

export const onOrderBetween = (before, after) => {
    if (!before && !after) return STEP;
    if (!before) return (after.order ?? STEP) - STEP;
    if (!after) return (before.order ?? 0) + STEP;
    return ((before.order ?? 0) + (after.order ?? 0)) / 2;
};

export const useKanban = ({ cards: initial = [], onMove = null } = {}) => {
    const [useCards, setCards] = useState(initial);
    const [useDragId, setDragId] = useState(null);
    const [useOverColumn, setOverColumn] = useState(null);

    const CB__onGetColumnCards = useCallback((columnId) => (
        useCards
            .filter((c) => c.columnId === columnId)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    ), [useCards]);

    /*
        forcedCardId evita depender de que setDragId haya terminado de
        renderizar. Es importante para controles tipo select/popover:
        antes se hacía setDragId(card.id) + onDrop() en el mismo tick y
        onDrop todavía leía el dragId anterior, por eso la tarjeta no se movía.
    */
    const CB__onDrop = useCallback(async (columnId, targetIndex = null, forcedCardId = null) => {
        const CARD_ID = forcedCardId ?? useDragId;
        const CARD = useCards.find((c) => c.id === CARD_ID);

        setDragId(null);
        setOverColumn(null);

        if (!CARD) return false;

        const COLUMN = useCards
            .filter((c) => c.columnId === columnId && c.id !== CARD.id)
            .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

        const INDEX = targetIndex === null ? COLUMN.length : targetIndex;
        const ORDER = onOrderBetween(COLUMN[INDEX - 1], COLUMN[INDEX]);

        if (CARD.columnId === columnId && CARD.order === ORDER) return true;

        const PREVIOUS = useCards;

        setCards((prev) => prev.map((c) => (
            c.id === CARD.id ? { ...c, columnId, order: ORDER } : c
        )));

        try {
            await onMove?.({ card: CARD, toColumnId: columnId, order: ORDER, index: INDEX });
            return true;
        } catch {
            setCards(PREVIOUS);
            return false;
        }
    }, [useCards, useDragId, onMove]);

    const CB__onMoveCard = useCallback((cardId, columnId, targetIndex = null) => (
        CB__onDrop(columnId, targetIndex, cardId)
    ), [CB__onDrop]);

    return {
        cards: useCards, setCards,
        dragId: useDragId, setDragId,
        overColumn: useOverColumn, setOverColumn,
        onGetColumnCards: CB__onGetColumnCards,
        onDrop: CB__onDrop,
        onMoveCard: CB__onMoveCard,
    };
};
